<template>
  <div class="partners-tab">
    <el-form :inline="true" size="small" class="filters" @submit.native.prevent="fetchData">
      <el-form-item label="Status">
        <el-select v-model="filters.status" style="width: 160px" @change="fetchData">
          <el-option label="All" value="all" />
          <el-option v-for="option in statusOptions" :key="option.value" :label="option.label" :value="option.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button icon="el-icon-refresh" :loading="loading" @click="fetchData">Refresh</el-button>
      </el-form-item>
      <el-form-item>
        <span class="muted">Latest 100 partners for this filter, newest applications first.</span>
      </el-form-item>
    </el-form>

    <el-table v-loading="loading" :data="items" border size="small" style="width: 100%" row-key="_id" empty-text="No partners for this filter">
      <el-table-column type="expand">
        <template slot-scope="scope">
          <el-descriptions :column="2" size="small" border class="expand-details">
            <el-descriptions-item label="User ID"><span class="mono">{{ idOf(scope.row) }}</span></el-descriptions-item>
            <el-descriptions-item label="Partner code"><span class="mono">{{ scope.row.code || '-' }}</span></el-descriptions-item>
            <el-descriptions-item label="Terms document"><span class="mono">{{ scope.row.terms_document_id || '-' }}</span></el-descriptions-item>
            <el-descriptions-item label="Terms version">{{ scope.row.terms_version ? `v${scope.row.terms_version}` : '-' }}</el-descriptions-item>
            <el-descriptions-item label="Invited">{{ formatTime(scope.row.created_at) }}</el-descriptions-item>
            <el-descriptions-item label="Re-invited">{{ formatTime(scope.row.reinvited_at) }}</el-descriptions-item>
            <el-descriptions-item label="Review reason">{{ scope.row.review_reason || '-' }}</el-descriptions-item>
            <el-descriptions-item label="Reviewed">{{ formatTime(scope.row.reviewed_at) }}</el-descriptions-item>
            <el-descriptions-item label="Status reason">{{ scope.row.status_reason || '-' }}</el-descriptions-item>
            <el-descriptions-item label="Status changed">{{ formatTime(scope.row.status_changed_at) }}</el-descriptions-item>
            <el-descriptions-item label="Manual payout note">{{ scope.row.manual_payout_note || '-' }}</el-descriptions-item>
            <el-descriptions-item label="Manual payout verified">{{ formatTime(scope.row.manual_payout_verified_at) }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </el-table-column>
      <el-table-column label="Partner" min-width="210">
        <template slot-scope="scope">
          <div class="strong">{{ scope.row.display_name || 'No application yet' }}</div>
          <div class="muted mono">{{ idOf(scope.row) }}</div>
          <div v-if="scope.row.code" class="muted">Code <span class="mono">{{ scope.row.code }}</span></div>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="110">
        <template slot-scope="scope">
          <el-tag size="small" :type="statusTag(scope.row.status)">{{ statusLabel(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Channel" min-width="200">
        <template slot-scope="scope">
          <a v-if="safeUrl(scope.row.channel_url)" :href="scope.row.channel_url" target="_blank" rel="noopener noreferrer nofollow" class="channel-link">{{ scope.row.channel_url }}</a>
          <span v-else>{{ scope.row.channel_url || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Country" width="80">
        <template slot-scope="scope">{{ scope.row.country || '-' }}</template>
      </el-table-column>
      <el-table-column label="Submitted" width="150">
        <template slot-scope="scope">
          <div>{{ formatTime(scope.row.submitted_at) }}</div>
          <div v-if="scope.row.terms_version" class="muted">Terms v{{ scope.row.terms_version }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Manual payout" width="130">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.manual_payout_verified_at" size="small" type="success">Verified</el-tag>
          <span v-else class="muted">Not verified</span>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="250" fixed="right">
        <template slot-scope="scope">
          <div class="actions">
            <el-button v-if="scope.row.status === 'submitted'" size="mini" type="primary" :disabled="anyPending" @click="openReview(scope.row)">Review</el-button>
            <el-button
              v-if="scope.row.status === 'approved'"
              size="mini"
              type="danger"
              plain
              :loading="isPending(`status:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="changeStatus(scope.row, 'suspended')"
            >
              Suspend
            </el-button>
            <el-button
              v-if="scope.row.status === 'suspended'"
              size="mini"
              type="success"
              plain
              :loading="isPending(`status:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="changeStatus(scope.row, 'approved')"
            >
              Reactivate
            </el-button>
            <el-button
              v-if="['approved', 'suspended'].includes(scope.row.status)"
              size="mini"
              :disabled="anyPending"
              @click="openManualPayout(scope.row)"
            >
              {{ scope.row.manual_payout_verified_at ? 'Unverify payout' : 'Verify payout' }}
            </el-button>
          </div>
          <div class="actions links">
            <el-button type="text" size="mini" @click="$emit('open-ledger', idOf(scope.row))">Ledger</el-button>
            <el-button type="text" size="mini" @click="$emit('open-payouts', idOf(scope.row))">Payout batches</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="Review application" :visible.sync="reviewVisible" width="620px" :close-on-click-modal="false">
      <template v-if="reviewTarget">
        <el-descriptions :column="1" size="small" border class="dialog-block">
          <el-descriptions-item label="Display name">{{ reviewTarget.display_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Channel URL">
            <a v-if="safeUrl(reviewTarget.channel_url)" :href="reviewTarget.channel_url" target="_blank" rel="noopener noreferrer nofollow" class="channel-link">{{ reviewTarget.channel_url }}</a>
            <span v-else>{{ reviewTarget.channel_url || '-' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="Country">{{ reviewTarget.country || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Submitted">{{ formatTime(reviewTarget.submitted_at) }}</el-descriptions-item>
          <el-descriptions-item label="Terms accepted">
            {{ reviewTarget.terms_version ? `v${reviewTarget.terms_version}` : '-' }}
            <span v-if="reviewTarget.terms_document_id" class="muted mono">({{ reviewTarget.terms_document_id }})</span>
          </el-descriptions-item>
          <el-descriptions-item label="User ID"><span class="mono">{{ idOf(reviewTarget) }}</span></el-descriptions-item>
        </el-descriptions>
        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item label="Decision" required>
            <el-radio-group v-model="reviewForm.decision">
              <el-radio-button label="approved">Approve</el-radio-button>
              <el-radio-button label="rejected">Reject</el-radio-button>
            </el-radio-group>
            <div class="muted field-help">
              {{ reviewForm.decision === 'approved'
                ? 'Approval issues a partner code; the partner can then create referral links.'
                : 'A rejected applicant can only apply again after a new invitation.' }}
            </div>
          </el-form-item>
          <el-form-item label="Reason" required>
            <el-input v-model="reviewForm.reason" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="Required: internal review note (not shown to the partner)" />
          </el-form-item>
        </el-form>
      </template>
      <span slot="footer">
        <el-button @click="reviewVisible = false">Cancel</el-button>
        <el-button
          :type="reviewForm.decision === 'approved' ? 'primary' : 'danger'"
          :loading="reviewTarget && isPending(`review:${idOf(reviewTarget)}`)"
          :disabled="anyPending || !reviewForm.decision || !reviewForm.reason.trim()"
          @click="submitReview"
        >
          {{ reviewForm.decision === 'approved' ? 'Approve application' : 'Reject application' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="Manual payout details" :visible.sync="manualVisible" width="600px" :close-on-click-modal="false">
      <template v-if="manualTarget">
        <el-alert
          title="Verify payee details outside LearningFirst."
          description="Check the partner's bank or Wise recipient directly through your finance process. LearningFirst only stores that the details were verified and your note. Never enter bank account numbers, IBANs, or other payment details here."
          type="warning"
          show-icon
          :closable="false"
          class="dialog-block"
        />
        <el-descriptions :column="1" size="small" border class="dialog-block">
          <el-descriptions-item label="Partner">{{ manualTarget.display_name || idOf(manualTarget) }}</el-descriptions-item>
          <el-descriptions-item label="Country">{{ manualTarget.country || '-' }}</el-descriptions-item>
          <el-descriptions-item label="Current state">
            <el-tag v-if="manualTarget.manual_payout_verified_at" size="small" type="success">Verified {{ formatTime(manualTarget.manual_payout_verified_at) }}</el-tag>
            <span v-else>Not verified</span>
          </el-descriptions-item>
        </el-descriptions>
        <el-form label-position="top" size="small" @submit.native.prevent>
          <el-form-item label="Change to" required>
            <el-radio-group v-model="manualForm.verified">
              <el-radio-button :label="true">Verified</el-radio-button>
              <el-radio-button :label="false">Not verified</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="Reason" required>
            <el-input
              v-model="manualForm.reason"
              type="textarea"
              :rows="3"
              maxlength="500"
              show-word-limit
              placeholder="Required: who verified and how, e.g. 'Finance confirmed Wise recipient by video call'. No account numbers."
            />
          </el-form-item>
        </el-form>
      </template>
      <span slot="footer">
        <el-button @click="manualVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          :loading="manualTarget && isPending(`manual:${idOf(manualTarget)}`)"
          :disabled="anyPending || !manualForm.reason.trim()"
          @click="submitManualPayout"
        >
          Save
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  listAffiliates,
  reviewAffiliate,
  setAffiliateManualPayout,
  setAffiliateStatus
} from '@/apis/lfAdmin'
import affiliateMixin, { AFFILIATE_STATUSES, labelFor } from './affiliateMixin'

export default {
  name: 'AffiliatePartnersTab',
  mixins: [affiliateMixin],
  data() {
    return {
      loading: false,
      items: [],
      statusOptions: AFFILIATE_STATUSES,
      filters: {
        status: 'all'
      },
      reviewVisible: false,
      reviewTarget: null,
      reviewForm: {
        decision: 'approved',
        reason: ''
      },
      manualVisible: false,
      manualTarget: null,
      manualForm: {
        verified: true,
        reason: ''
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    statusLabel(status) {
      return labelFor(AFFILIATE_STATUSES, status)
    },
    statusTag(status) {
      const map = { approved: 'success', submitted: 'warning', rejected: 'danger', suspended: 'danger', closed: 'info', invited: '' }
      return map[status] || ''
    },
    safeUrl(url) {
      return typeof url === 'string' && /^https?:\/\//i.test(url)
    },
    async fetchData() {
      this.loading = true
      const params = this.filters.status !== 'all' ? { status: this.filters.status } : {}
      const data = await this.loadData(() => listAffiliates(params), 'Failed to load partners')
      if (data !== undefined) this.items = Array.isArray(data) ? data : []
      this.loading = false
    },
    openReview(row) {
      this.reviewTarget = row
      this.reviewForm = { decision: 'approved', reason: '' }
      this.reviewVisible = true
    },
    async submitReview() {
      const row = this.reviewTarget
      if (!row) return
      const id = this.idOf(row)
      const decision = this.reviewForm.decision
      const reason = this.reviewForm.reason.trim()
      if (!reason) return
      const confirmed = await this.confirmAction({
        title: decision === 'approved' ? 'Approve application' : 'Reject application',
        lines: [
          `${decision === 'approved' ? 'Approve' : 'Reject'} the application from ${row.display_name || id}?`,
          `Reason: ${reason}`
        ],
        confirmText: decision === 'approved' ? 'Approve' : 'Reject',
        type: decision === 'approved' ? 'info' : 'warning'
      })
      if (!confirmed) return
      const result = await this.runMutation(`review:${id}`, () => reviewAffiliate({ user_id: id, decision, reason }), {
        failure: 'Failed to save review'
      })
      if (result) {
        const code = result.data && result.data.code
        this.$message.success(decision === 'approved' ? `Approved${code ? ` with code ${code}` : ''}` : 'Application rejected')
        this.reviewVisible = false
        this.fetchData()
      }
    },
    async changeStatus(row, status) {
      const id = this.idOf(row)
      const suspending = status === 'suspended'
      const reason = await this.confirmWithReason({
        title: suspending ? 'Suspend partner' : 'Reactivate partner',
        lines: [
          `${suspending ? 'Suspend' : 'Reactivate'} ${row.display_name || id}?`,
          suspending
            ? 'Referral links stop earning and payouts cannot be prepared until the partner is reactivated.'
            : 'The partner returns to approved status and can earn again.'
        ],
        confirmText: suspending ? 'Suspend' : 'Reactivate',
        type: suspending ? 'error' : 'warning',
        placeholder: 'Required: internal reason (not shown to the partner)'
      })
      if (reason === null) return
      const result = await this.runMutation(`status:${id}`, () => setAffiliateStatus({ user_id: id, status, reason }), {
        success: suspending ? 'Partner suspended' : 'Partner reactivated',
        failure: 'Failed to update partner status'
      })
      if (result) this.fetchData()
    },
    openManualPayout(row) {
      this.manualTarget = row
      this.manualForm = { verified: !row.manual_payout_verified_at, reason: '' }
      this.manualVisible = true
    },
    async submitManualPayout() {
      const row = this.manualTarget
      if (!row) return
      const id = this.idOf(row)
      const verified = this.manualForm.verified === true
      const reason = this.manualForm.reason.trim()
      if (!reason) return
      const confirmed = await this.confirmAction({
        title: verified ? 'Mark payout details verified' : 'Mark payout details unverified',
        lines: [
          verified
            ? `Confirm that ${row.display_name || id}'s bank or Wise details were verified outside LearningFirst?`
            : `Remove the manual payout verification for ${row.display_name || id}? Manual payout batches cannot be prepared until it is verified again.`,
          `Reason: ${reason}`
        ],
        confirmText: 'Save',
        type: 'warning'
      })
      if (!confirmed) return
      const result = await this.runMutation(`manual:${id}`, () => setAffiliateManualPayout({ affiliate_id: id, verified, reason }), {
        success: verified ? 'Manual payout marked verified' : 'Manual payout marked unverified',
        failure: 'Failed to save manual payout status'
      })
      if (result) {
        this.manualVisible = false
        this.fetchData()
      }
    }
  }
}
</script>

<style scoped>
.filters {
  padding: 12px 12px 0;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.channel-link {
  color: #409eff;
  word-break: break-all;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.actions .el-button + .el-button {
  margin-left: 0;
}

.actions.links {
  margin-top: 4px;
}

.expand-details {
  padding: 4px 12px;
}

.dialog-block {
  margin-bottom: 14px;
}

.field-help {
  margin-top: 4px;
  line-height: 1.4;
}
</style>
